import type { User } from "@/types";
import { UserRole } from "@/types";
import type { Permissions } from "@/hooks/usePermissions";
import type { ModuleKey, ResolvedModules } from "@/lib/modules/registry";
import { ALL_MODULES_ENABLED } from "@/lib/modules/registry";

/**
 * Candidate landing pages, most specific first, each paired with the EXACT guard
 * its route carries in AppRouter. The pairing is the whole point: a landing page
 * chosen by any other criterion can be refused on arrival, and since the refusal
 * handler itself calls this function, a refused landing is an infinite redirect
 * — /planning bounces to /planning and the tab hangs with nothing in the console.
 *
 * Each candidate also names the module that owns it — and, where its route is
 * gated on a sub-feature rather than the whole module, that feature too: a route
 * whose module OR feature the league has disabled is refused on arrival exactly
 * like a permission-less one, so the module matrix has to filter the candidates
 * for the same anti-loop reason the guards do. Checking only the module was a
 * reachable dead end: `payments_treasury` on with `finances_dashboard` off sent
 * a finances-only user to /finances → refused → /404, with /virements and /etats
 * still perfectly usable.
 *
 * Keep in sync with AppRouter's ProtectedRoute guards. `landingRoute.test.ts`
 * asserts every role in ROLE_MATRIX lands somewhere it is actually allowed.
 */
const CANDIDATES: ReadonlyArray<{
    path: string;
    moduleKey: ModuleKey;
    /** Only when the route's own ProtectedRoute carries a featureKey. */
    featureKey?: string;
    guard: (p: Pick<Permissions, "can">) => boolean;
}> = [
    { path: "/planning", moduleKey: "planning", guard: (p) => p.can("view", "dashboard") },
    // Gate scanners (security_gate) land straight on the scan mode: scanning is
    // their whole job, and /media only shows them the log. Same guard as the
    // /media/scan route. Anyone with `view dashboard` (super_admin) still stops
    // at /planning first.
    { path: "/media/scan", moduleKey: "media_accreditation", guard: (p) => p.can("scan", "media_access_log") },
    { path: "/media", moduleKey: "media_accreditation", guard: (p) => p.can("view", "media") },
    { path: "/accounting", moduleKey: "accounting", guard: (p) => p.can("view", "accounting") },
    { path: "/finances", moduleKey: "payments_treasury", featureKey: "finances_dashboard", guard: (p) => p.can("view", "finances") },
    // Les deux autres pages du module trésorerie, sur la même permission : sans
    // elles, `finances_dashboard` coupé renvoyait un utilisateur finances-only
    // sur /404 alors que /virements et /etats restaient parfaitement ouverts.
    { path: "/virements", moduleKey: "payments_treasury", featureKey: "virements", guard: (p) => p.can("view", "finances") },
    { path: "/etats", moduleKey: "payments_treasury", featureKey: "etats", guard: (p) => p.can("view", "finances") },
    { path: "/disciplinary", moduleKey: "disciplinary", guard: (p) => p.can("view", "disciplinary") },
    { path: "/calendar", moduleKey: "calendar_generation", guard: (p) => p.can("view", "calendar") },
    { path: "/classements", moduleKey: "rankings", guard: (p) => p.can("view", "rankings") },
    { path: "/officials-management", moduleKey: "officials", guard: (p) => p.can("view", "officials") },
    { path: "/clubs", moduleKey: "club_registry", featureKey: "clubs", guard: (p) => p.can("view", "clubs") },
];

/**
 * Modules qui peuvent servir de page d'accueil, dérivés de CANDIDATES (dédupliqués,
 * ordre de déclaration). Le panneau Settings → Modules s'en sert pour avertir
 * qu'une ligue vient de couper sa dernière destination possible ; le dériver ici
 * évite qu'une recopie manuelle dérive du tableau réel.
 */
export const LANDING_CANDIDATE_MODULES: ModuleKey[] = [
    ...new Set(CANDIDATES.map((c) => c.moduleKey)),
];

/**
 * Where to send a user who may not stay where they are — both the post-login
 * destination and the target of every permission refusal.
 *
 * `/planning` remains the default for the broad roles, but it is itself gated on
 * `view dashboard`, and eight roles in the matrix do not have that permission
 * (accounting_manager, the three accountant tiers, both disciplinary roles,
 * team_manager, and now security_gate). For those, "just send them to /planning"
 * was a hang, not a redirect.
 *
 * `/404` is the terminal fallback: it is un-gated, so it cannot loop. An account
 * with no grants at all gets an honest dead end instead of a frozen tab.
 *
 * REFREE_MANAGER is special-cased ahead of the permission chain because its
 * landing page is a preference, not a permission — that role *can* see the
 * dashboard, it just shouldn't start there.
 *
 * `modules` defaults to all-enabled so callers that predate the league module
 * matrix keep their exact behaviour; AppRouter passes the resolved matrix.
 */
export function resolveLandingRoute(
    user: User | null | undefined,
    permissions: Pick<Permissions, "can">,
    modules: ResolvedModules = ALL_MODULES_ENABLED,
): string {
    const isRefreeManager =
        user?.role === UserRole.REFREE_MANAGER ||
        user?.primaryRole === UserRole.REFREE_MANAGER ||
        (user?.roles ?? []).some((r) => r.role === UserRole.REFREE_MANAGER);

    // Only while the league still has the module: /officials-management carries
    // moduleKey="officials" in AppRouter, so short-circuiting onto a disabled
    // module would be refused on arrival and loop back here. Fall through to the
    // candidate chain instead.
    if (isRefreeManager && modules.isModuleEnabled("officials")) {
        return "/officials-management";
    }

    return (
        CANDIDATES.find(
            (c) =>
                modules.isModuleEnabled(c.moduleKey) &&
                (!c.featureKey || modules.isFeatureEnabled(c.moduleKey, c.featureKey)) &&
                c.guard(permissions),
        )?.path ?? "/404"
    );
}
